import { Controller } from 'stimulus';
import gtag from '@/tracking/gtag';
import toArray from '@/helpers/toArray';

/**
 *  Initialize event handlers for steps.
 *
 * @class StepsController
 * @extends {Controller}
 */
class StepsController extends Controller {
  connect() {
    this.score = null;
  }

  /**
   * Send form data to back-end.
   */
  sendForm() {
    window.axios.post(window.app.api.formStore, new FormData(this.element))
      .then((res) => {
        this.score = res.data.totalScore;

        gtag.event(
          'Test voltooid',
          'Score behaald',
          this.score >= 3 ? 'Score: >= 3' : 'Score: < 3',
        );

        this.outputResult(res.data);
      });
  }

  /**
   * Output data from response to the page.
   *
   * @param data
   */
  outputResult(data) {
    this.pdf = data.pdf;
    this.totalScoreTarget.innerHTML = this.score;
    this.resultTextTarget.innerHTML = this.score >= 3
      ? this.resultTextTarget.dataset.resultHigherThanText
      : this.resultTextTarget.dataset.resultLowerThanText;
  }

  /**
   * Handle click on the download button.
   */
  onDownloadClick() {
    this.downloadFile();
  }

  /**
   * Download the results.
   */
  downloadFile() {
    window.open(`${window.app.api.formDownload}/${this.pdf}`);

    gtag.event(
      'Downloaden van resultaat',
      'click',
      this.score >= 3 ? 'Score: >= 3' : 'Score: < 3',
    );
  }

  /**
   * Handle click on arrows, scroll to next or previous step.
   *
   * @param e
   */
  onNextPrevStepClick(e) {
    const { stepTarget } = e.currentTarget.dataset;
    const currentStep = this.wrapperTarget.querySelector(`[data-step="${stepTarget}"]`);

    if (this.shouldSendForm(stepTarget, e.currentTarget)) this.sendForm();

    gtag.event('Wisselen van stap', 'click', `Naar: ${currentStep.dataset.stepTitle}`);

    this.activateStep(currentStep);
    StepsController.scrollToStep(this.wrapperTarget, stepTarget);
  }

  /**
   * Handle selection of answers.
   * Activate next arrow if current step has an answer selected.
   */
  onAnswerSelect(e) {
    this.onNextPrevStepClick(e);

    const previousStep = e.currentTarget.closest('[data-target="steps.step"]');

    toArray(previousStep.querySelectorAll('[data-next-step]')).forEach((button) => {
      button.disabled = false;
    });
  }

  /**
   * Mark all steps as inactive then mark current step as active.
   *
   * @param currentStep
   */
  activateStep(currentStep) {
    this.stepTargets.forEach((step) => step.classList.remove('active'));
    currentStep.classList.toggle('active');
  }

  /**
   * Enable the next arrow if the current step has an answer selected.
   *
   * @param state
   */
  activateNextStepButton(state) {
    toArray(this.getCurrentStep().querySelectorAll('[data-next-step]')).forEach((button) => {
      button.disabled = !state;
    });
  }

  /**
   * Enable the next arrow if the current step has an answer selected.
   *
   * @param stepElement
   * @param state
   */
  // eslint-disable-next-line class-methods-use-this
  activatePreviousStepButton(stepElement, state) {
    toArray(stepElement.querySelectorAll('[data-next-step]')).forEach((button) => {
      button.disabled = !state;
    });
  }

  /**
   * Return true or false whether the current step has an answer selected.
   *
   * @returns Boolean
   */
  isStepAnswered() {
    return this.getAnswersForCurrentStep().filter((answer) => answer.checked).length > 0;
  }

  /**
   * Return true or false whether the current step is final step
   * And arrow is next
   *
   * @param stepTarget
   * @returns Boolean
   */
  shouldSendForm(stepTarget) {
    return parseInt(stepTarget, 10) === this.stepTargets.length - 1;
  }

  /**
   * Return current step as HTMLElement.
   *
   * @returns HTMLElement
   */
  getCurrentStep() {
    return this.stepTargets.filter((step) => step.classList.contains('active'))[0];
  }

  /**
   * Returns all available answers for the current step.
   *
   * @returns Array
   */
  getAnswersForCurrentStep() {
    return toArray(this.getCurrentStep().querySelectorAll('[data-target="steps.answer"]'));
  }

  /**
   * Transform the steps wrapper to simulate scroll to step.
   *
   * @param element
   * @param transform
   */
  static setTransform(element, transform) {
    element.style.webkitTransform = transform;
    element.style.msTransform = transform;
    element.style.transform = transform;
  }

  /**
   * Calculate the translate value then call the transform function
   *
   * @param elementToTranslate
   * @param step
   */
  static scrollToStep(elementToTranslate, step) {
    const translate = `translateX(-${step * 100}vw)`;
    StepsController.setTransform(elementToTranslate, translate);
  }
}

StepsController.targets = [
  'step',
  'answer',
  'wrapper',
  'totalScore',
  'resultText',
];

export default StepsController;
